import { useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';

import { FeedItemFromAPI, FeedItemMetaData } from '../../../../interfaces/Feed';
import { useGetProfileFeedQuery } from '../../../../queries/ProfileFeed';
import useToggle from '../../../../hooks/useToggle';
import useFlowFeed from '../../../../hooks/useFlowFeed';
import { FLOWS, RECOGNITION_PARTICIPATE } from '../../../../constants/routes';
import useGetParsedFilterOptions from '../../../../hooks/useGetParsedFilterOptions';
import {
  useProfileFeedUpdateFlowPostReactionMutation,
  useProfileFeedUpdatePostReactionMutation,
} from '../../../../queries/Feed';
import { useFetchMembersDetailsQuery } from '../../../../queries/MembersDetails';
import { transformMemberToAutoCompleteDropdownItem } from '../utils';
import { PostTypes } from '../../../../interfaces/Home';
import useHistoryWrapper from '../../../../hooks/useHistoryWrapper';
import useReactionUpdatedSocket from '../../../../hooks/useReactionUpdatedSocket';
import { PageType } from '../../../../hooks/useReactionUpdatedSocket/types';

export const useProfileFeedController = () => {
  const history = useHistoryWrapper();

  const { filter } = useGetParsedFilterOptions();
  const { userId } = useParams<{ userId: string }>();
  const { data: selectedProfileData } = useFetchMembersDetailsQuery([userId]);

  const { mutate: mutateFlowPostReaction } =
    useProfileFeedUpdateFlowPostReactionMutation({ userId, filter });

  const { mutate: mutatePostReaction } =
    useProfileFeedUpdatePostReactionMutation({ userId, filter });

  const { feedContextProps, isProfileInfoLoading, profileData } = useFlowFeed({
    mutateFlowPostReaction,
    mutatePostReaction,
  });

  const {
    models: { toggleValue: isRecognitionFlowModalOpen },
    operations: { setToggleValue: toggleIsRecognitionFlowModalOpen },
  } = useToggle();

  useReactionUpdatedSocket({
    pageType: PageType.ProfileFeed,
    filter: filter,
    profileId: userId,
  });

  const handleCelebrateTeammateClick = useCallback(() => {
    history.push(`${RECOGNITION_PARTICIPATE}?redirectUrl=${location.pathname}`);
  }, [history]);

  const handleEditPostClick = useCallback(
    (flow: FeedItemMetaData) => {
      if (flow.type === PostTypes.FLOW) {
        const editPostUrl = `${FLOWS}/${flow.flowId}/edit/${flow.responseId}?redirectUrl=${location.pathname}`;
        history.push(editPostUrl);
      } else {
        const editPostUrl = `${FLOWS}/recognition/edit/${flow.postId}?redirectUrl=${location.pathname}`;
        history.push(editPostUrl);
      }
    },
    [history],
  );

  const {
    data,
    refetch,
    isLoading: isFeedLoading,
    hasNextPage,
    fetchNextPage,
  } = useGetProfileFeedQuery({ userId, filter });

  const isLoading = isFeedLoading || isProfileInfoLoading;

  const feedProps = useMemo(() => {
    if (!data) {
      return [];
    }
    return data.pages.reduce<FeedItemFromAPI[]>(
      (arr, page) => [...arr, ...page.data],
      [],
    );
  }, [data]);

  const isOwnProfileFeed = useMemo(() => {
    return profileData && profileData.member.memberId === userId;
  }, [profileData, userId]);

  const selectedProfileUser = useMemo(() => {
    if (selectedProfileData && selectedProfileData.membersDetails?.length > 0) {
      return selectedProfileData.membersDetails[0];
    }
    return null;
  }, [selectedProfileData]);

  const profileDataForRecognitionModalDropdown = useMemo(() => {
    if (isOwnProfileFeed || !selectedProfileData) {
      return undefined;
    }

    const memberDetails = selectedProfileData.membersDetails[0];
    return transformMemberToAutoCompleteDropdownItem(memberDetails);
  }, [isOwnProfileFeed, selectedProfileData]);

  return {
    models: {
      isLoading,
      hasNextPage,
      profileData,
      selectedProfileUser,
      isOwnProfileFeed,
      data: feedProps,
      isRecognitionFlowModalOpen,
      profileDataForRecognitionModalDropdown,
    },
    operations: {
      refetch,
      handleFetchNextPage: fetchNextPage,
      toggleIsRecognitionFlowModalOpen,
      feedContextProps,
      handleEditPostClick,
      handleCelebrateTeammateClick,
    },
  };
};
